import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '../notifications/notification.service';
import { TekstResourcePipe } from '../pipes/tekstresource.pipe';
import { ErrorService } from './error.service';

@Injectable()
export class BomErrorHandler implements ErrorHandler {
  constructor(private injector: Injector, private tekstResourcePipe: TekstResourcePipe) { }

  handleError(error: Error | HttpErrorResponse): void {
    // We use the injector to get required services since injecting them directly in the constructor crashes the app
    const notificationService = this.injector.get(NotificationService);
    const errorService = this.injector.get(ErrorService);
    const router = this.injector.get(Router);

    if (error instanceof HttpErrorResponse) {
      // Server error

      if (!navigator.onLine) {
        // No internet connection
        notificationService.warn(this.tekstResourcePipe.transform('NoInternet'));
      } else if (error.status === 403) {
        // Redirect if forbidden
        router.navigate(['/error'], {
          queryParams: {
            date: new Date(),
            statusCode: 403,
          },
          skipLocationChange: true,
        });
      } else if (error.status >= 400 && error.status < 500) {
        // Show warning for 4xx errors
        notificationService.warn(error.error || error.message);
      } else {
        // Show error for other errors
        let message = this.tekstResourcePipe.transform('Fejl.Notifikation.Part1', {
          errorCode: error.status,
        });

        if (error.headers.has('x-correlation-id')) {
          message +=
            '. ' +
            this.tekstResourcePipe.transform('Fejl.Notifikation.Part2', {
              correlationId: error.headers.get('x-correlation-id'),
            });
        }

        notificationService.error(message);
      }
    } else {
      // Client error
      let status: string | undefined;
      if (error.message?.indexOf('iat is in the future') >= 0) {
        status = 'iat';
      } else if (error.message?.indexOf('exp is in the past') >= 0) {
        status = 'exp';
      }

      // Log the error - then navigate
      errorService.log(error).subscribe({
        next: response => {
          router.navigate(['/error'], {
            queryParams: { correlationid: response.correlationId, statusCode: status, date: new Date() },
          });
        },
        error: () => {
          // If we fail to log the client error on server, transfer it to the error page
          // through storage to avoid url length limitations for some browsers
          const errorId = errorService.logToStorage(error);
          router.navigate(['/error'], { queryParams: { date: new Date(), errorId } });
        },
      });
    }

    // Display the error in the console
    console.error(error);
  }
}
